import React, { useState, useEffect } from "react";
import { useTeamData, useAppStore, type TeamRole, CURRENCY_RATES } from "../stores/appStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart";
import { AreaChart, Area, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  UserCheck,
  Target,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { createEmptyArray, validateSalary, validateHeadcount, formatCurrency, safeMath } from "../lib/utils";



interface TeamStatsProps {
  teamRoles: TeamRole[];
}

function TeamStats({ teamRoles }: TeamStatsProps) {
  const getTotalHeadcount = (monthIndex: number) => {
    return teamRoles.reduce((sum, role) => safeMath.add(sum, role.headcount[monthIndex] || 0), 0);
  };

  const getTotalSalaryCost = (monthIndex: number) => {
    return teamRoles.reduce((sum, role) => safeMath.add(sum, safeMath.multiply(role.salary, role.headcount[monthIndex] || 0)), 0);
  };

  // Use the utility function for consistent formatting

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Total Roles
              </p>
              <p className="text-2xl font-bold text-foreground">
                {teamRoles.length}
              </p>
            </div>
            <Users className="w-8 h-8 text-primary" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Current Headcount
              </p>
              <p className="text-2xl font-bold text-green-600">
                {getTotalHeadcount(0)}
              </p>
            </div>
            <UserCheck className="w-8 h-8 text-green-600" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
               <p className="text-sm text-muted-foreground mb-1">
                 Month 22 Headcount
               </p>
               <p className="text-2xl font-bold text-blue-600">
                 {getTotalHeadcount(21)}
              </p>
            </div>
            <Target className="w-8 h-8 text-blue-600" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Current Salary Cost
              </p>
              <p className="text-2xl font-bold text-foreground">
                {formatCurrency(getTotalSalaryCost(0))}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-indigo-600" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface HeadcountSelectorProps {
  value: number;
  onChange: (value: number) => void;
  maxDisplay?: number;
  className?: string;
}

function HeadcountSelector({ value, onChange, maxDisplay = 10, className = "" }: HeadcountSelectorProps) {
  const handlePersonClick = (index: number) => {
    // If clicking on a filled person, reduce count
    // If clicking on an empty person, increase count
    if (index < value) {
      // Clicking on filled person - reduce by 1
      onChange(Math.max(0, value - 1));
    } else {
      // Clicking on empty person - increase to this index + 1
      onChange(index + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(Math.min(99, value + 1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(Math.max(0, value - 1));
    }
  };

  const displayCount = Math.min(value, maxDisplay);
  const hasOverflow = value > maxDisplay;

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      {/* Visual Person Indicators */}
      <div
        className="flex gap-0.5 cursor-pointer select-none"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="spinbutton"
        aria-valuemin={0}
        aria-valuemax={99}
        aria-valuenow={value}
        aria-label={`Headcount: ${value} people`}
      >
        {Array.from({ length: maxDisplay }, (_, index) => (
          <div
            key={index}
            onClick={() => handlePersonClick(index)}
            className={`
              w-4 h-4 border rounded transition-all duration-150 flex items-center justify-center text-[8px] font-bold
              ${index < value
                ? 'bg-green-500 border-green-600 text-white'
                : 'bg-gray-100 border-gray-300 text-gray-400 hover:bg-gray-200 hover:border-gray-400'
              }
            `}
          >
            {index < value ? '●' : '○'}
          </div>
        ))}
        {hasOverflow && (
          <div className="w-4 h-4 border border-orange-300 bg-orange-100 rounded flex items-center justify-center text-[8px] font-bold text-orange-700">
            +{value - maxDisplay}
          </div>
        )}
      </div>

      {/* Compact Numeric Display */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          className="w-4 h-4 rounded border border-gray-300 hover:bg-gray-100 flex items-center justify-center text-[10px] font-bold"
          aria-label="Decrease headcount"
        >
          ▼
        </button>

        <div className="min-w-[2rem] text-center font-semibold text-sm">
          {value}
        </div>

        <button
          onClick={() => onChange(Math.min(99, value + 1))}
          className="w-4 h-4 rounded border border-gray-300 hover:bg-gray-100 flex items-center justify-center text-[10px] font-bold"
          aria-label="Increase headcount"
        >
          ▲
        </button>
      </div>
    </div>
  );
}

interface RoleCardProps {
  role: TeamRole;
  onEdit: (role: TeamRole) => void;
  onDelete: (id: string) => void;
  onUpdateHeadcount: (roleId: string, monthIndex: number, value: number) => void;
}

function CompactRoleCard({ role, onEdit, onDelete, onUpdateHeadcount }: RoleCardProps) {
  const { currency } = useAppStore();

  const formatCurrency = (amount: number) => {
    const convertedValue = amount * CURRENCY_RATES[currency];
    const currencyConfig = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };
    const config = currencyConfig[currency];
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
    }).format(convertedValue);
  };

  const getCurrencySymbol = () => {
    const currencySymbols = {
      USD: '$',
      AED: 'د.إ',
      INR: '₹',
    };
    return currencySymbols[currency];
  };

  return (
    <Card className="h-full">
      <CardContent className="p-4">
        {/* Role Header - Compact */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-sm truncate">{role.name}</h3>
             <p className="text-xs text-muted-foreground mt-1">
               {role.hourlyRate ? `${getCurrencySymbol()}${role.hourlyRate}/hr • ` : ''}{formatCurrency(role.salary)} • {role.category}
             </p>
          </div>
          <div className="flex gap-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(role)}
              className="h-6 w-6 p-0"
            >
              <Edit className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(role.id)}
              className="h-6 w-6 p-0"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Ultra Compact Headcount Grid */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground mb-2">Monthly Headcount</div>
          <div className="grid grid-cols-9 gap-1">
            {role.headcount.slice(0, 22).map((count, monthIndex) => (
              <div key={monthIndex} className="flex flex-col items-center gap-1">
                <div className="text-[10px] text-muted-foreground font-medium text-center leading-tight">
                  {monthIndex + 1}
                </div>
                <HeadcountSelector
                  value={count}
                  onChange={(value) => onUpdateHeadcount(role.id, monthIndex, value)}
                  maxDisplay={2}
                  className="scale-50 origin-center"
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface AddRoleDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingRole: TeamRole | null;
  newRole: Partial<TeamRole>;
  setNewRole: (role: Partial<TeamRole>) => void;
  onSave: () => void;
}

function AddRoleDialog({ isOpen, onOpenChange, editingRole, newRole, setNewRole, onSave }: AddRoleDialogProps) {
  const { currency } = useAppStore();

  const getCurrencySymbol = () => {
    const currencySymbols = {
      USD: '$',
      AED: 'د.إ',
      INR: '₹',
    };
    return currencySymbols[currency];
  };
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Role
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingRole ? 'Edit Team Role' : 'Add New Team Role'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="roleName">Role Name</Label>
            <Input
              id="roleName"
              value={newRole.name || ''}
              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              placeholder="e.g. Senior Engineer"
            />
          </div>
            <div className="space-y-2">
              <Label htmlFor="hourlyRate">Hourly Rate ({getCurrencySymbol()}/hr) - Optional</Label>
             <Input
               id="hourlyRate"
               type="number"
               value={newRole.hourlyRate || ''}
               onChange={(e) => {
                 const hourlyRate = e.target.value ? Number(e.target.value) : undefined;
                 const updates: Partial<TeamRole> = { hourlyRate };
                 if (hourlyRate) {
                   // Auto-calculate salary from hourly rate
                   updates.salary = Math.round(hourlyRate * 2080);
                 }
                 setNewRole({ ...newRole, ...updates });
               }}
               placeholder="50"
               step="0.01"
             />
           </div>
           <div className="space-y-2">
             <Label htmlFor="salary">Annual Salary (₹)</Label>
             <Input
               id="salary"
               type="number"
               value={newRole.salary || ''}
               onChange={(e) => setNewRole({ ...newRole, salary: Number(e.target.value), hourlyRate: undefined })}
               placeholder="120000"
             />
             {newRole.hourlyRate && (
               <p className="text-xs text-muted-foreground">
                 Auto-calculated from ${newRole.hourlyRate}/hr
               </p>
             )}
           </div>
          <div className="space-y-2">
            <Label htmlFor="category">Department</Label>
            <Select
              value={newRole.category || 'engineering'}
              onValueChange={(value: TeamRole['category']) =>
                setNewRole({ ...newRole, category: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="founder">Founder</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="support">Support</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            {editingRole ? 'Update' : 'Add'} Role
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TeamRolesManagerProps {
  teamRoles: TeamRole[];
  onAddRole: () => void;
  onEditRole: (role: TeamRole) => void;
  onDeleteRole: (id: string) => void;
  onUpdateHeadcount: (roleId: string, monthIndex: number, value: number) => void;
  isAddDialogOpen: boolean;
  setIsAddDialogOpen: (open: boolean) => void;
  editingRole: TeamRole | null;
  newRole: Partial<TeamRole>;
  setNewRole: (role: Partial<TeamRole>) => void;
  onSaveRole: () => void;
}

// Enterprise Table Component for Headcount Management
interface TeamHeadcountTableProps {
  teamRoles: TeamRole[];
  onEditRole: (role: TeamRole) => void;
  onDeleteRole: (id: string) => void;
  onUpdateHeadcount: (roleId: string, monthIndex: number, value: number) => void;
  onSetQuarterHeadcount?: (roleId: string, quarterIndex: number, value: number) => void;
}

function TeamHeadcountTable({
  teamRoles,
  onEditRole,
  onDeleteRole,
  onUpdateHeadcount,
  onSetQuarterHeadcount,
}: TeamHeadcountTableProps) {
  const { planningMonths, currency } = useAppStore();

  const formatCurrency = (amount: number) => {
    const convertedValue = amount * CURRENCY_RATES[currency];
    const currencyConfig = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };
    const config = currencyConfig[currency];
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
    }).format(convertedValue);
  };

  const getCurrencySymbol = () => {
    const currencySymbols = {
      USD: '$',
      AED: 'د.إ',
      INR: '₹',
    };
    return currencySymbols[currency];
  };

  const getCategoryBadgeColor = (category: TeamRole['category']) => {
    const colors = {
      founder: 'badge-founder',
      engineering: 'badge-engineering',
      design: 'badge-design',
      operations: 'badge-operations',
      sales: 'badge-sales',
      support: 'badge-support',
    };
    return colors[category] || 'badge-engineering';
  };

  // Function to set all months in a quarter to a specific value
  const setQuarterHeadcount = (roleId: string, quarterIndex: number, value: number) => {
    const startMonth = quarterIndex * 3;
    const endMonth = Math.min(startMonth + 3, planningMonths);

    for (let monthIndex = startMonth; monthIndex < endMonth; monthIndex++) {
      onUpdateHeadcount(roleId, monthIndex, value);
    }
  };

  // Function to get quarter total for a role
  const getQuarterTotal = (role: TeamRole, quarterIndex: number) => {
    const startMonth = quarterIndex * 3;
    const endMonth = Math.min(startMonth + 3, planningMonths);
    return role.headcount.slice(startMonth, endMonth).reduce((sum, count) => sum + count, 0);
  };

  // Function to get color class based on headcount value
  const getHeadcountColorClass = (value: number) => {
    if (value === 0) return 'color-info-bg text-gray-500 dark:text-gray-400';
    if (value >= 1 && value <= 2) return 'color-warning-bg';
    if (value >= 3 && value <= 5) return 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-300';
    return 'color-success-bg';
  };

  if (teamRoles.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">No team roles defined</h3>
        <p className="text-sm text-muted-foreground mb-4">Add your first team role to start planning headcount</p>
      </div>
  );
}

  return (
    <div className="space-y-4">
      {/* Usage Guide & Color Legend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
          <CardContent className="p-3">
            <div className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-[10px] font-bold">i</span>
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-100">Planning Guide</h4>
                <div className="text-[10px] text-blue-800 dark:text-blue-200 space-y-0.5">
                  <p>• <strong>Quarter buttons (+)</strong> set all months to 1</p>
                  <p>• <strong>Arrow keys</strong> navigate between cells</p>
                  <p>• <strong>Compact design</strong> for 22-month planning</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Color Legend */}
        <Card className="border-gray-200 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-950/20">
          <CardContent className="p-3">
            <div className="space-y-1">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">Headcount Indicators</h4>
              <div className="grid grid-cols-2 gap-1">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-700"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Empty (0)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-orange-50 border-orange-200 dark:bg-orange-950 dark:border-orange-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Low (1-2)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">Medium (3-5)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800"></div>
                  <span className="text-[10px] text-gray-700 dark:text-gray-300">High (6+)</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1400px]" style={{
            backgroundImage: `
              linear-gradient(to right, transparent 0%, transparent calc(25% - 2px), rgba(59, 130, 246, 0.1) calc(25% - 2px), rgba(59, 130, 246, 0.1) calc(25% + 2px), transparent calc(25% + 2px)),
              linear-gradient(to right, transparent 0%, transparent calc(50% - 2px), rgba(59, 130, 246, 0.1) calc(50% - 2px), rgba(59, 130, 246, 0.1) calc(50% + 2px), transparent calc(50% + 2px)),
              linear-gradient(to right, transparent 0%, transparent calc(75% - 2px), rgba(59, 130, 246, 0.1) calc(75% - 2px), rgba(59, 130, 246, 0.1) calc(75% + 2px), transparent calc(75% + 2px))
            `,
            backgroundSize: '100% 100%'
          }}>
            {/* Sticky Header with Quarter Groups */}
            <thead className="bg-muted/70 sticky top-0 z-10">
              {/* Quarter Group Headers */}
              <tr className="border-b-2 border-primary/20">
                <th colSpan={4} className="px-3 py-1.5 bg-muted/50 border-r-2 border-primary/30"></th>
                {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => (
                  <th
                    key={`quarter-${quarterIndex}`}
                    colSpan={4} // 3 months + 1 total column
                    className="px-2 py-2 text-center bg-primary/5 border-r-2 border-primary/30 last:border-r-0"
                  >
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-primary">Q{quarterIndex + 1}</span>
                      </div>
                      <div className="text-[10px] font-semibold text-primary">
                        M{quarterIndex * 3 + 1}-{Math.min((quarterIndex + 1) * 3, planningMonths)}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
              {/* Column Headers */}
              <tr className="border-b border-border/50">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Role
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Dept
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50">
                  Salary
                </th>
                <th className="px-2 py-2 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border/30 bg-muted/50 w-16">
                  Actions
                </th>
                {/* Month columns and quarter totals */}
                {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => {
                  const months = [1, 2, 3].map(m => quarterIndex * 3 + m).filter(m => m <= planningMonths);
                  return (
                    <React.Fragment key={`quarter-group-${quarterIndex}`}>
                      {/* Month columns */}
                      {months.map((month) => (
                        <th
                          key={`month-${month - 1}`}
                          className="px-2 py-2 text-center text-[10px] font-semibold min-w-[50px] border-r border-border/30 bg-muted/50"
                        >
                          <div className="text-muted-foreground font-medium">
                            {month}
                          </div>
                        </th>
                      ))}
                      {/* Quarter Total Column */}
                      <th className="px-2 py-2 text-center text-[10px] font-semibold min-w-[55px] border-r-2 border-primary/30 bg-primary/10">
                        <div className="space-y-0.5">
                          <div className="text-primary font-bold text-[9px]">Q{quarterIndex + 1}</div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-3 w-3 p-0 text-primary hover:bg-primary/20"
                            onClick={() => {
                              // Set all roles in this quarter to 1
                              teamRoles.forEach(role => {
                                if (onSetQuarterHeadcount) {
                                  onSetQuarterHeadcount(role.id, quarterIndex, 1);
                                } else {
                                  setQuarterHeadcount(role.id, quarterIndex, 1);
                                }
                              });
                            }}
                            title={`Set all Q${quarterIndex + 1} positions to 1`}
                          >
                            <span className="text-[8px] font-bold">+</span>
                          </Button>
                        </div>
                      </th>
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/20">
              {teamRoles.map((role, roleIndex) => (
                <tr key={role.id} className={`hover:bg-muted/30 transition-all duration-150 ${
                  roleIndex % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                }`}>
                  {/* Role Name */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-3 h-3 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground text-sm">{role.name}</div>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${getCategoryBadgeColor(role.category)}`}>
                      {role.category.charAt(0).toUpperCase() + role.category.slice(1)}
                    </span>
                  </td>

                  {/* Salary */}
                  <td className="px-3 py-2 border-r border-border/30">
                    <div className="text-xs font-bold text-foreground">
                      {formatCurrency(role.salary)}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-2 py-2 border-r border-border/30">
                    <div className="flex items-center justify-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditRole(role)}
                        className="h-6 w-6 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                        title="Edit role"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteRole(role.id)}
                        className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                        title="Delete role"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>



                  {/* Headcount inputs for each month and quarter totals */}
                  {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => {
                    const months = [1, 2, 3].map(m => quarterIndex * 3 + m).filter(m => m <= planningMonths);
                    return (
                      <React.Fragment key={`quarter-group-${quarterIndex}`}>
                        {/* Month columns */}
                        {months.map((month) => {
                          const monthIndex = month - 1;
                          const count = role.headcount[monthIndex] || 0;
                          const colorClass = getHeadcountColorClass(count);
                          return (
                            <td
                              key={`month-${monthIndex}`}
                              className="px-2 py-2 text-center border-r border-border/30 bg-muted/20"
                            >
                              <div className="relative">
                                <Input
                                  type="number"
                                  min="0"
                                  max="999"
                                  value={count || ''}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || 0;
                                    onUpdateHeadcount(role.id, monthIndex, Math.max(0, value));
                                  }}
                                  onKeyDown={(e) => {
                                    // Allow tab navigation and common keyboard shortcuts
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      const input = e.target as HTMLInputElement;
                                      input.select();
                                    }
                                    // Arrow key navigation between cells
                                    if (e.key === 'ArrowRight' && !e.shiftKey) {
                                      e.preventDefault();
                                      const nextCell = e.currentTarget.closest('td')?.nextElementSibling?.querySelector('input');
                                      nextCell?.focus();
                                    }
                                    if (e.key === 'ArrowLeft' && !e.shiftKey) {
                                      e.preventDefault();
                                      const prevCell = e.currentTarget.closest('td')?.previousElementSibling?.querySelector('input');
                                      prevCell?.focus();
                                    }
                                  }}
                                  className={`w-10 h-6 text-center text-xs font-medium border rounded transition-all px-0.5 ${colorClass} hover:shadow-sm focus:shadow-md focus:ring-1`}
                                  placeholder="0"
                                  title={`Month ${month} headcount for ${role.name}`}
                                  aria-label={`${role.name} - Month ${month} headcount`}
                                />
                              </div>
                            </td>
                          );
                        })}
                        {/* Quarter Total Column */}
                        <td className="px-2 py-2 text-center border-r-2 border-primary/30 bg-primary/5">
                          <div className="flex flex-col items-center gap-0.5">
                            <div className={`text-xs font-bold px-1.5 py-0.5 rounded ${getHeadcountColorClass(getQuarterTotal(role, quarterIndex))}`}>
                              {getQuarterTotal(role, quarterIndex)}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-primary hover:bg-primary/20"
                              onClick={() => {
                                if (onSetQuarterHeadcount) {
                                  onSetQuarterHeadcount(role.id, quarterIndex, 1);
                                } else {
                                  setQuarterHeadcount(role.id, quarterIndex, 1);
                                }
                              }}
                              title={`Set all Q${quarterIndex + 1} months to 1 for ${role.name}`}
                            >
                              <span className="text-[8px] font-bold">+</span>
                            </Button>
                          </div>
                        </td>
                      </React.Fragment>
                     );
                   })}
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compact Summary Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Total Headcount Summary */}
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Total Team Size</p>
                 <p className="text-lg font-bold text-foreground">
                   {teamRoles.reduce((sum, role) => sum + Math.max(...role.headcount), 0)}
                 </p>
                <p className="text-[10px] text-muted-foreground">Peak headcount (Month 22)</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Growth Summary */}
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Avg. Monthly Growth</p>
                 <p className="text-lg font-bold text-foreground">
                   {teamRoles.length > 0
                     ? Math.round(teamRoles.reduce((sum, role) => {
                         const growth = role.headcount[17] - role.headcount[0];
                         return sum + (growth > 0 ? growth / 17 : 0);
                       }, 0) / teamRoles.length * 10) / 10
                     : 0
                   }
                 </p>
                <p className="text-[10px] text-muted-foreground">People per month</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cost Summary */}
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Total Annual Cost</p>
                 <p className="text-lg font-bold text-foreground">
                   {formatCurrency(teamRoles.reduce((sum, role) => sum + (role.salary * Math.max(...role.headcount)), 0))}
                 </p>
                <p className="text-[10px] text-muted-foreground">At peak headcount</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compact Quarterly Summary */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" />
            Quarterly Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className={`grid gap-2 ${Math.ceil(planningMonths / 3) <= 3 ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-6'}`}>
            {Array.from({ length: Math.ceil(planningMonths / 3) }, (_, quarterIndex) => {
              const quarterStart = quarterIndex * 3;
              const quarterEnd = Math.min((quarterIndex + 1) * 3 - 1, planningMonths - 1);
              const quarterTotal = teamRoles.reduce((sum, role) => {
                return sum + role.headcount.slice(quarterStart, quarterEnd + 1).reduce((qSum, count) => qSum + count, 0);
              }, 0);
              const avgHeadcount = Math.round(quarterTotal / (quarterEnd - quarterStart + 1));

              return (
                <div key={quarterIndex} className={`text-center p-2 rounded border ${getHeadcountColorClass(avgHeadcount)}`}>
                  <div className="text-sm font-bold">{avgHeadcount}</div>
                  <div className="text-[10px] opacity-75">Q{quarterIndex + 1}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Resource Usage Chart */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" />
            Resource Usage Over Time
          </CardTitle>
          <CardDescription>
            Headcount growth and resource allocation trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ChartContainer
              config={{
                headcount: {
                  label: "Total Headcount",
                  color: "hsl(var(--chart-1))",
                },
              }}
              className="h-full w-full"
            >
              <AreaChart
                data={Array.from({ length: planningMonths }, (_, monthIndex) => {
                  const totalHeadcount = teamRoles.reduce((sum, role) => sum + (role.headcount[monthIndex] || 0), 0);
                  const totalCost = teamRoles.reduce((sum, role) => sum + (role.salary * (role.headcount[monthIndex] || 0)), 0);
                  return {
                    month: monthIndex + 1,
                    headcount: totalHeadcount,
                    cost: totalCost,
                    monthLabel: `M${monthIndex + 1}`,
                  };
                })}
                margin={{
                  left: 12,
                  right: 12,
                  top: 12,
                  bottom: 12,
                }}
              >
                <defs>
                  <linearGradient id="headcountGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey="monthLabel"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  interval={Math.max(0, Math.floor(planningMonths / 12) - 1)}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                  domain={[0, 'dataMax + 5']}
                />
                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col">
                              <span className="text-[10px] uppercase text-muted-foreground">
                                {label}
                              </span>
                              <span className="font-bold text-sm">
                                {data.headcount} people
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] uppercase text-muted-foreground">
                                Cost
                              </span>
                              <span className="font-bold text-sm">
                                {formatCurrency(data.cost)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="headcount"
                  stroke="hsl(var(--chart-1))"
                  fillOpacity={1}
                  fill="url(#headcountGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TeamRolesManager({
  teamRoles,
  onAddRole,
  onEditRole,
  onDeleteRole,
  onUpdateHeadcount,
  isAddDialogOpen,
  setIsAddDialogOpen,
  editingRole,
  newRole,
  setNewRole,
  onSaveRole,
}: TeamRolesManagerProps) {
  const { planningMonths } = useAppStore();

  // Function to set all months in a quarter to a specific value for a specific role
  const setQuarterHeadcount = (roleId: string, quarterIndex: number, value: number) => {
    const startMonth = quarterIndex * 3;
    const endMonth = Math.min(startMonth + 3, planningMonths);

    for (let monthIndex = startMonth; monthIndex < endMonth; monthIndex++) {
      onUpdateHeadcount(roleId, monthIndex, value);
    }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Roles & Headcount Planning
            </CardTitle>
            <CardDescription>
              Define team roles and plan headcount growth over {planningMonths} months
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Planning Period Settings */}
            <div className="flex items-center gap-2">
              <Label htmlFor="planning-months" className="text-xs font-medium">
                Planning Period:
              </Label>
              <Select
                value={planningMonths.toString()}
                onValueChange={(value) => {
                  const months = parseInt(value);
                  if (months >= 6 && months <= 36) {
                    // Update planning months in store
                    useAppStore.getState().setPlanningMonths(months);
                    // Resize all team headcount arrays
                    useAppStore.getState().resizeTeamHeadcounts(months);
                  }
                }}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="22">22 months</SelectItem>
                  <SelectItem value="24">24 months</SelectItem>
                  <SelectItem value="36">36 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <AddRoleDialog
              isOpen={isAddDialogOpen}
              onOpenChange={setIsAddDialogOpen}
              editingRole={editingRole}
              newRole={newRole}
              setNewRole={setNewRole}
              onSave={onSaveRole}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Enterprise Table View */}
          <TeamHeadcountTable
            teamRoles={teamRoles}
            onEditRole={onEditRole}
            onDeleteRole={onDeleteRole}
            onUpdateHeadcount={onUpdateHeadcount}
            onSetQuarterHeadcount={setQuarterHeadcount}
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface HiringPlanningProps {
  teamRoles: TeamRole[];
}

function HiringPlanning({ teamRoles }: HiringPlanningProps) {
  const { currency } = useAppStore();

  const getTotalHeadcount = (monthIndex: number) => {
    return teamRoles.reduce((sum, role) => sum + role.headcount[monthIndex], 0);
  };

  const getTotalSalaryCost = (monthIndex: number) => {
    return teamRoles.reduce((sum, role) => sum + (role.salary * role.headcount[monthIndex]), 0);
  };

  const formatCurrency = (amount: number) => {
    const convertedValue = amount * CURRENCY_RATES[currency];
    const currencyConfig = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };
    const config = currencyConfig[currency];
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
    }).format(convertedValue);
  };

  const getCurrencySymbol = () => {
    const currencySymbols = {
      USD: '$',
      AED: 'د.إ',
      INR: '₹',
    };
    return currencySymbols[currency];
  };

  const getCategoryColor = (category: TeamRole['category']) => {
    const colors = {
      founder: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      engineering: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      design: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
      operations: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      sales: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      support: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    };
    return colors[category] || colors.engineering;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Headcount Growth Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Headcount Growth
          </CardTitle>
          <CardDescription>
            Team size progression over 22 months
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 22 }, (_, monthIndex) => (
              <div key={monthIndex} className="flex items-center justify-between">
                <span className="text-sm font-medium">Month {monthIndex + 1}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {getTotalHeadcount(monthIndex)} people
                  </span>
                  <span className="text-sm font-medium">
                    {formatCurrency(getTotalSalaryCost(monthIndex))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Role Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Current Team Distribution
          </CardTitle>
          <CardDescription>
            Breakdown by department and role
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {teamRoles
              .filter(role => role.headcount[0] > 0)
              .map((role) => (
                <div key={role.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={getCategoryColor(role.category)}>
                      {role.category}
                    </Badge>
                    <span className="text-sm font-medium">{role.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{role.headcount[0]}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatCurrency(role.salary * role.headcount[0])}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function JobsAndPeople() {
  const {
    teamRoles,
    addTeamRole,
    updateTeamRole,
    deleteTeamRole,
    updateHeadcount
  } = useTeamData();
  const state = useAppStore();
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<TeamRole | null>(null);

  // New role form state
  const [newRole, setNewRole] = useState<Partial<TeamRole>>({
        name: '',
        salary: 0,
        headcount: createEmptyArray(state.planningMonths, 0),
        category: 'engineering',
  });

  // Auto-save team data to startup when it changes
  useEffect(() => {
    if (teamRoles.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const startupId = urlParams.get('startupId');

      if (startupId) {
        const savedStartups = localStorage.getItem('userStartups');
        if (savedStartups) {
          try {
            const startups = JSON.parse(savedStartups);
            const startupIndex = startups.findIndex((s: any) => s.id === startupId);

            if (startupIndex >= 0) {
              // Update startup with current team data
              startups[startupIndex] = {
                ...startups[startupIndex],
                updatedAt: new Date(),
                data: {
                  ...startups[startupIndex].data,
                  teamRoles: teamRoles
                }
              };
              localStorage.setItem('userStartups', JSON.stringify(startups));
            }
          } catch (error) {
            console.error('Error auto-saving team data:', error);
          }
        }
      }
    }
  }, [teamRoles]);

  const handleAddRole = () => {
    const validatedSalary = validateSalary(newRole.salary || 0);
    if (newRole.name?.trim() && validatedSalary > 0) {
      addTeamRole({
        name: newRole.name.trim(),
        salary: validatedSalary,
        headcount: newRole.headcount || createEmptyArray(state.planningMonths, 0),
        category: newRole.category as TeamRole['category'] || 'engineering',
      });

      // Reset form
      setNewRole({
        name: '',
        salary: 0,
        headcount: createEmptyArray(state.planningMonths, 0),
        category: 'engineering',
      });
      setIsAddRoleOpen(false);
    }
  };

  const handleEditRole = (role: TeamRole) => {
    setEditingRole(role);
    setNewRole(role);
    setIsAddRoleOpen(true);
  };

  const handleUpdateRole = () => {
    const validatedSalary = validateSalary(newRole.salary || 0);
    if (editingRole && newRole.name?.trim() && validatedSalary > 0) {
      updateTeamRole(editingRole.id, {
        name: newRole.name.trim(),
        salary: validatedSalary,
        headcount: newRole.headcount || createEmptyArray(state.planningMonths, 0),
        category: newRole.category as TeamRole['category'] || 'engineering',
      });
      setEditingRole(null);
      setIsAddRoleOpen(false);
    }
  };

  const handleDeleteRole = (id: string) => {
    deleteTeamRole(id);
  };



  return (
    <div className="space-y-6">
      <TeamStats teamRoles={teamRoles} />

      <Tabs defaultValue="roles" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="roles">Team Roles & Headcount</TabsTrigger>
          <TabsTrigger value="planning">Hiring Planning</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-6">
          <TeamRolesManager
            teamRoles={teamRoles}
            onAddRole={() => setIsAddRoleOpen(true)}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            onUpdateHeadcount={updateHeadcount}
            isAddDialogOpen={isAddRoleOpen}
            setIsAddDialogOpen={setIsAddRoleOpen}
            editingRole={editingRole}
            newRole={newRole}
            setNewRole={setNewRole}
            onSaveRole={editingRole ? handleUpdateRole : handleAddRole}
          />
        </TabsContent>

        <TabsContent value="planning" className="space-y-6">
          <HiringPlanning teamRoles={teamRoles} />
        </TabsContent>
      </Tabs>
    </div>
  );
}